// src/components/Dashboard/Dashboard.js

import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useLocation } from 'react-router-dom';
import { useFeatureFlags } from '../../contexts/FeatureFlagProvider';
import QuickCaptureCard from '../quick-capture/QuickCaptureCard';
import NotesWorkspace from '../notes/NotesWorkspace';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
  IconButton,
  Paper,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  TextField,
  Button,
  Divider,
} from '@mui/material';
import {
  Today as TodayIcon,
  Add as AddIcon,
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckIcon,
  Star as StarIcon,
  Schedule as ScheduleIcon,
} from '@mui/icons-material';
import NotesList from '../NotesList';

const todayTasks = [
  { id: 1, text: 'Review morning voice notes', completed: false, priority: 'high' },
  { id: 2, text: 'Organize project ideas', completed: true, priority: 'medium' },
  { id: 3, text: 'Reflect on daily insights', completed: false, priority: 'low' },
];

function Dashboard() {
  const { currentUser, userProfile } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const featureFlags = useFeatureFlags();
  const isUIv2Enabled = featureFlags.get('notes.ui.v2');
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tasks, setTasks] = useState(todayTasks);
  const [newTask, setNewTask] = useState('');
  const isToday = location.pathname === '/today';
  
  const toggleTask = (taskId) => {
    setTasks(tasks.map(task => 
      task.id === taskId ? { ...task, completed: !task.completed } : task
    ));
  };

  const addTask = () => {
    if (newTask.trim()) {
      setTasks([...tasks, {
        id: Date.now(),
        text: newTask.trim(),
        completed: false,
        priority: 'medium'
      }]);
      setNewTask('');
    }
  };

  const completedTasks = tasks.filter(task => task.completed).length;
  const totalTasks = tasks.length;

  // Use new Notes UI v2 if enabled
  if (isUIv2Enabled) {
    return <NotesWorkspace />;
  }

  return (
    <Box sx={{ 
      minHeight: '100vh',
      bgcolor: 'background.default',
      p: { xs: 2, md: 3 },
    }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <TodayIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography 
            variant={isMobile ? "h5" : "h4"} 
            fontWeight={700}
            color="primary.main"
          >
            {isToday ? 'Today' : 'Inbox'}
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          {isToday 
            ? `Good ${new Date().getHours() < 12 ? 'morning' : new Date().getHours() < 18 ? 'afternoon' : 'evening'}, ${userProfile?.displayName || userProfile?.name || currentUser?.displayName || currentUser?.email?.split('@')[0] || 'there'}!`
            : 'All your captured ideas and voice notes'
          }
        </Typography>
      </Box>

      {/* Quick Capture Card - Always at top */}
      <Box sx={{ mb: 3 }}>
        <QuickCaptureCard />
      </Box>

      <Grid container spacing={3}>
        {/* Today Plan - Pinned at top (only show on Today view) */}
        {isToday && (
          <Grid item xs={12}>
            <Paper 
              elevation={0}
              sx={{ 
                p: 3, 
                border: `1px solid ${theme.palette.primary.main}20`,
                bgcolor: `${theme.palette.primary.main}05`,
                borderRadius: 3,
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="h6" fontWeight={600} color="primary.main">
                  📋 Today's Plan
                </Typography>
                <Chip 
                  label={`${completedTasks}/${totalTasks} completed`}
                  color={completedTasks === totalTasks ? 'success' : 'primary'}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <List dense sx={{ mb: 2 }}>
                {tasks.map((task) => (
                  <ListItem 
                    key={task.id}
                    disablePadding
                    sx={{ 
                      borderRadius: 2,
                      mb: 0.5,
                      '&:hover': { bgcolor: 'action.hover' }
                    }}
                  >
                    <IconButton 
                      size="small" 
                      onClick={() => toggleTask(task.id)}
                      sx={{ mr: 1 }}
                    >
                      {task.completed ? (
                        <CheckIcon color="success" />
                      ) : (
                        <UncheckIcon color="action" />
                      )}
                    </IconButton>
                    <ListItemText 
                      primary={task.text}
                      primaryTypographyProps={{
                        sx: {
                          textDecoration: task.completed ? 'line-through' : 'none',
                          color: task.completed ? 'text.secondary' : 'text.primary',
                          fontSize: '0.9rem',
                        }
                      }}
                    />
                    <Chip 
                      label={task.priority}
                      size="small"
                      color={
                        task.priority === 'high' ? 'error' :
                        task.priority === 'medium' ? 'warning' : 'default'
                      }
                      variant="outlined"
                      sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                    />
                  </ListItem>
                ))}
              </List>

              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  placeholder="Add a task for today..."
                  value={newTask}
                  onChange={(e) => setNewTask(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && addTask()}
                  sx={{ flex: 1 }}
                />
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={addTask}
                  disabled={!newTask.trim()}
                >
                  <AddIcon fontSize="small" />
                </Button>
              </Box>
            </Paper>
          </Grid>
        )}


        {/* Notes Section */}
        <Grid item xs={12}>
          <Card elevation={0} sx={{ 
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 3,
            minHeight: 400,
          }}>
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" fontWeight={600}>
                  📝 {isToday ? 'Today\'s Notes' : 'All Notes'}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Chip 
                    icon={<StarIcon />}
                    label="Recent"
                    size="small"
                    variant="outlined"
                    clickable
                  />
                  <Chip 
                    icon={<ScheduleIcon />}
                    label="Voice"
                    size="small"
                    variant="outlined"
                    clickable
                  />
                </Box>
              </Box>
              <Divider sx={{ mb: 2 }} />
              <NotesList />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

    </Box>
  );
}

export default Dashboard;
